import styles from './ListEmptyState.module.css';

// Skeleton cell layout enum matching the original grid system
export enum SkeletonCellLayout {
  ONE_OF_TWELVE = '8.33%',
  TWO_OF_TWELVE = '16.67%',
  THREE_OF_TWELVE = '25%',
  FOUR_OF_TWELVE = '33.33%',
  FIVE_OF_TWELVE = '41.67%',
  SIX_OF_TWELVE = '50%',
  SEVEN_OF_TWELVE = '58.33%',
  EIGHT_OF_TWELVE = '66.67%',
  NINE_OF_TWELVE = '75%',
  TEN_OF_TWELVE = '83.33%',
  ELEVEN_OF_TWELVE = '91.67%',
  TWELVE_OF_TWELVE = '100%',
}

type ListEmptyStateHeadingProps = {
  children: React.ReactNode;
};

export function ListEmptyStateHeading({ children }: ListEmptyStateHeadingProps) {
  return <h2 className={styles.heading}>{children}</h2>;
}

type ListEmptyStateDescriptionProps = {
  children: React.ReactNode;
};
export function ListEmptyStateDescription({ children }: ListEmptyStateDescriptionProps) {
  return <div className={styles.description}>{children}</div>;
}

type ListEmptyStateActionsProps = {
  children: React.ReactNode;
};
export function ListEmptyStateActions({ children }: ListEmptyStateActionsProps) {
  return <div className={styles.actions}>{children}</div>;
}

type ListEmptyStateProps = {
  children: React.ReactNode;
};
export function ListEmptyState({ children }: ListEmptyStateProps) {
  return (
    <div className={styles.container} data-list-empty-state>
      {children}
    </div>
  );
}

// Loading skeleton components
type ListEmptyStateLoadingProps = {
  children: React.ReactNode[];
};

export function ListEmptyStateLoading({ children }: ListEmptyStateLoadingProps) {
  return (
    <div className={styles.gridSkeleton} role="progressbar" aria-label="loading" aria-valuetext="loading">
      {children}
    </div>
  );
}

export { ListEmptyStateLoadingWithEnvironments } from './ListEmptyStateLoadingWithEnvironments';

type SkeletonCellProps = {
  children?: React.ReactNode;
  className?: string;
  layout?: SkeletonCellLayout | string | number; // Accept enum, custom string, or number
};

export function SkeletonCell({ children, className, layout }: SkeletonCellProps) {
  let style = {};

  if (layout) {
    if (typeof layout === 'number') {
      style = { flexBasis: `${layout}fr` };
    } else {
      style = { flexBasis: layout };
    }
  }

  return (
    <div className={`${styles.skeletonCell} ${className || ''}`} style={style}>
      {children || <SkeletonCellContent />}
    </div>
  );
}

export function SkeletonCellContent() {
  return <span className={`${styles.skeletonContent} ${styles.skeletonLoading}`} />;
}

// Enhanced utility for creating skeleton cells with specific layouts
export const createSkeletonCell = (layout: SkeletonCellLayout | string | number, content?: React.ReactNode) => (
  <SkeletonCell layout={layout}>
    {content || <span className={`${styles.skeletonContent} ${styles.skeletonLoadingThin}`} />}
  </SkeletonCell>
);

export const renderSkeletonLoading = (numberOfSkeletonRows: number) =>
  Array.from({ length: numberOfSkeletonRows }, (_, i) => (
    <SkeletonCell key={i}>
      <span className={`${styles.skeletonContent} ${styles.skeletonLoadingThin}`} />
    </SkeletonCell>
  ));
